import React, { useState, useCallback, FC, useMemo } from 'react';
import { Form, Input, Button, Modal, CopyBox } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { useLoadIntegrationTokens, useCreateIntegrationToken } from 'src/models/company/Tokens/hooks';

interface IFormState {
  tokenName: string;
}

interface IProps {}
export const TokensActions: FC<IProps> = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { refetch } = useLoadIntegrationTokens();
  const [tokenGenerated, updateTokenGeneratedState] = useState(false);
  const [createToken, { loading: tokenCreating }] = useCreateIntegrationToken();
  const [token, setToken] = useState('');

  const [createTokenVisible, setCreateTokenVisible] = useState(false);
  const onOpen = useCallback(() => {
    setCreateTokenVisible(true);
  }, []);
  const onClose = useCallback(() => {
    form.resetFields();
    setCreateTokenVisible(false);
    updateTokenGeneratedState(false);
  }, [form]);
  const onBack = useCallback(() => {
    updateTokenGeneratedState(false);
  }, []);

  const initialValues = useMemo((): IFormState => {
    return {
      tokenName: ''
    };
  }, []);

  const onFinish = useCallback(
    (values: IFormState) => {
      const { tokenName } = values;
      createToken({ variables: { name: tokenName } })
        .then(data => {
          const token = data.data?.createIntegrationToken.token;
          setToken(token || '');
          refetch();
          form.resetFields();
          updateTokenGeneratedState(true);
        })
        .catch(() => {
          form.setFields([
            {
              name: 'tokenName',
              errors: [t('Common.unknownErrorDesc')]
            }
          ]);
        });
    },
    [createToken, form, refetch, updateTokenGeneratedState, t]
  );

  return (
    <>
      <div className={styles.contentInner}>
        <Button type={'primary'} onClick={onOpen}>
          {t('Tokens.generateToken')}
        </Button>
      </div>
      {createTokenVisible && (
        <Modal
          title={<span>{t(!tokenGenerated ? 'Tokens.addNewTokenTitle' : 'Tokens.generatedNewTokenTitle')}</span>}
          visible
          onCancel={onClose}
          maskClosable
          width={500}
          footer={null}
          keyboard={false}
        >
          <div className={styles.modalContent}>
            {!tokenGenerated ? (
              <>
                <p className={styles.desc}>{t('Tokens.generateTokenDesc')}</p>
                <Form
                  form={form}
                  onFinish={onFinish}
                  className={styles.formContent}
                  layout={'vertical'}
                  initialValues={initialValues}
                >
                  <Form.Item
                    label={t('Tokens.tokenName')}
                    name="tokenName"
                    rules={[
                      { required: true, message: t('Common.inputRequired', { inputName: t('Tokens.tokenName') }) }
                    ]}
                  >
                    <Input type="text" data-name="tokenName" placeholder={t('Tokens.tokenName')} />
                  </Form.Item>
                  <div className={styles.formSubmit}>
                    <Button type={'text'} onClick={onClose} className={styles.button}>
                      {t('Common.cancel')}
                    </Button>
                    <Button type={'primary'} htmlType={'submit'} loading={tokenCreating} className={styles.button}>
                      {t('Tokens.addToken')}
                    </Button>
                  </div>
                </Form>
              </>
            ) : (
              <>
                <p className={styles.desc}>{t('Tokens.tokensCopyNote')}</p>
                <CopyBox value={token} />
                <div className={styles.formSubmit}>
                  <Button type={'text'} onClick={onClose} className={styles.button}>
                    {t('Common.cancel')}
                  </Button>
                  <Button type={'primary'} onClick={onBack} className={styles.button}>
                    {t('Tokens.addAnotherToken')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default TokensActions;
